import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2, Check } from 'lucide-react';
import { NmiCollectCheckout } from './NmiCollectCheckout';
import { PaymentMethodCard } from './PaymentMethodCard';
import {
  usePaymentMethods,
  useDeletePaymentMethod,
  useCreateCharge,
  useSubscriptionPlans,
  useCreateSubscription,
} from '@/hooks/usePayment';

interface CheckoutFormProps {
  onComplete?: () => void;
}

export function CheckoutForm({ onComplete }: CheckoutFormProps) {
  const { toast } = useToast();
  const { refetch: refetchUser } = useAuth();
  const [step, setStep] = useState<'plan' | 'method' | 'confirm'>('plan');
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);

  const { data: methodsData, isLoading: methodsLoading } = usePaymentMethods();
  const { data: plansData, isLoading: plansLoading, error: plansError, refetch: refetchPlans } = useSubscriptionPlans();
  const deleteMethod = useDeletePaymentMethod();
  const createCharge = useCreateCharge();
  const createSubscription = useCreateSubscription();

  const paymentMethods = methodsData?.paymentMethods || [];
  const plans = plansData?.plans || [];

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);

  const handleDeleteMethod = async (id: string) => {
    try {
      await deleteMethod.mutateAsync(id);
      toast({ title: 'Payment method removed' });
    } catch (error) {
      toast({
        title: 'Failed to remove',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPlanId || !selectedPaymentMethodId) return;

    try {
      await createSubscription.mutateAsync({
        planId: selectedPlanId,
        paymentMethodId: selectedPaymentMethodId,
      });
      toast({ title: 'Subscription created successfully' });
      await refetchUser();
      onComplete?.();
    } catch (error) {
      toast({
        title: 'Subscription failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  if (methodsLoading || plansLoading) {
    return (
      <Card className="glass">
        <CardContent className="pt-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {step === 'plan' && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium">Select a Plan</h3>
          {plansError && (
            <div className="p-4 rounded bg-destructive/10 text-destructive text-sm">
              Failed to load plans. <Button variant="link" className="p-0 h-auto" onClick={() => refetchPlans()}>Retry</Button>
            </div>
          )}
          {!plansLoading && plans.length === 0 && !plansError && (
            <div className="p-4 rounded bg-muted text-muted-foreground text-sm">
              No plans available. <Button variant="link" className="p-0 h-auto" onClick={() => refetchPlans()}>Refresh</Button>
            </div>
          )}
          <div className="grid gap-3">
            {plans.map((plan) => (
              <Card
                key={plan.id}
                className={`cursor-pointer transition-colors ${selectedPlanId === plan.id ? 'border-primary bg-primary/5' : 'glass'}`}
                onClick={() => setSelectedPlanId(plan.id)}
              >
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{plan.name}</p>
                    <p className="text-sm text-muted-foreground">
                      ${(plan.amountCents / 100).toFixed(2)}/{plan.interval}
                    </p>
                    {plan.trialDays > 0 && (
                      <p className="text-xs text-primary">{plan.trialDays}-day free trial</p>
                    )}
                  </div>
                  {selectedPlanId === plan.id && <Check className="h-5 w-5 text-primary" />}
                </CardContent>
              </Card>
            ))}
          </div>
          <Button
            className="w-full"
            disabled={!selectedPlanId}
            onClick={() => setStep('method')}
          >
            Continue
          </Button>
        </div>
      )}

      {step === 'method' && selectedPlan && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Payment Method</h3>
            <Button variant="ghost" size="sm" onClick={() => setStep('plan')}>
              Change Plan
            </Button>
          </div>

          <Card className="glass">
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">{selectedPlan.name}</p>
                  <p className="text-sm text-muted-foreground">
                    ${(selectedPlan.amountCents / 100).toFixed(2)}/{selectedPlan.interval}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <NmiCollectCheckout plan={selectedPlan} disabled={false} />

          {paymentMethods.length > 0 && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or use saved card</span>
                </div>
              </div>

              <div className="space-y-3">
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    onClick={() => setSelectedPaymentMethodId(method.id)}
                    className={`cursor-pointer ${selectedPaymentMethodId === method.id ? 'ring-2 ring-primary rounded-lg' : ''}`}
                  >
                    <PaymentMethodCard
                      method={method}
                      onDelete={handleDeleteMethod}
                      onSetDefault={() => {}}
                    />
                  </div>
                ))}
              </div>

              <Button
                className="w-full"
                disabled={!selectedPaymentMethodId}
                onClick={() => setStep('confirm')}
              >
                Continue with Selected Card
              </Button>
            </>
          )}
        </div>
      )}

      {step === 'confirm' && selectedPlan && (
        <Card className="glass">
          <CardHeader>
            <CardTitle className="text-base">Confirm Subscription</CardTitle>
            <CardDescription className="text-xs">Review your selection before confirming</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm">Plan</span>
              <span className="font-medium">{selectedPlan.name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Amount</span>
              <span className="font-medium">${(selectedPlan.amountCents / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Billing</span>
              <span className="font-medium capitalize">{selectedPlan.interval}ly</span>
            </div>
            <Separator />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep('method')}>
                Back
              </Button>
              <Button
                className="flex-1"
                onClick={handleSubscribe}
                disabled={createSubscription.isPending}
              >
                {createSubscription.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirm & Subscribe
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
